import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, Plus, MapPin } from "lucide-react";
import AddressForm from "@/components/AddressForm";

export default function Checkout() {
  const nav = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { items, totals, clear } = useCart();
  const [addresses, setAddresses] = useState([]);
  const [selectedAddr, setSelectedAddr] = useState(null);
  const [showNewAddr, setShowNewAddr] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState(null);
  const [payMethod, setPayMethod] = useState("payu"); // 'payu' | 'cod'

  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav("/auth?next=/checkout"); return; }
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  const reload = async () => {
    const { data } = await api.get("/addresses");
    setAddresses(data);
    const def = data.find((a) => a.is_default) || data[0];
    if (def) { setSelectedAddr(def.id); setShowNewAddr(false); }
    else setShowNewAddr(true);
  };

  const applyCoupon = async () => {
    try {
      const { data } = await api.post("/coupons/apply", { code: coupon, subtotal: totals.subtotal });
      setApplied(data);
      toast.success(`Coupon applied — ₹${data.discount.toFixed(0)} off`);
    } catch (e) {
      setApplied(null);
      toast.error(e?.response?.data?.detail || "Invalid coupon");
    }
  };

  const discount = applied?.discount || 0;
  const shipping = totals.subtotal - discount >= 999 ? 0 : 49;
  const total = Math.max(0, totals.subtotal - discount) + shipping;

  const placeOrder = async () => {
    if (items.length === 0) return toast.error("Bag is empty");
    const address = addresses.find((a) => a.id === selectedAddr);
    if (!address) return toast.error("Select or add a delivery address");
    if (!address.latitude || !address.longitude) return toast.error("Delivery address is missing map location");
    setPlacing(true);
    try {
      const payload = {
        items: items.map((it) => ({
          product_id: it.product_id, slug: it.slug, name: it.name,
          image: it.image, price: it.price, quantity: it.quantity,
          subscription: it.subscription,
        })),
        address, payment_method: payMethod,
        coupon_code: applied?.code || null,
      };
      const { data } = await api.post("/orders", payload);
      if (payMethod === "payu") {
        // Kick off PayU hosted checkout — clear cart AFTER we successfully get the signed form
        // so a checkout API failure doesn't strand the customer with an empty bag.
        try {
          const { data: pay } = await api.post("/payu/checkout", { order_number: data.order_number });
          clear();
          const form = document.createElement("form");
          form.method = "POST";
          form.action = pay.action;
          Object.entries(pay.fields).forEach(([k, v]) => {
            const input = document.createElement("input");
            input.type = "hidden"; input.name = k; input.value = v ?? "";
            form.appendChild(input);
          });
          document.body.appendChild(form);
          form.submit();
          return; // browser is navigating away
        } catch (e) {
          // Order was created but payment session failed — send them to result page to retry
          nav(`/order-result?number=${data.order_number}&status=failed`);
          return;
        }
      }
      clear();
      nav(`/order-success?number=${data.order_number}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not place order");
    }
    setPlacing(false);
  };

  if (authLoading) return <div className="max-w-[1400px] mx-auto py-32 text-center text-ink-500">Loading…</div>;

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16">
      <div className="eyebrow">Checkout</div>
      <h1 className="mt-3 font-display text-5xl text-ink-900">Almost yours.</h1>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 space-y-10">
          {/* ADDRESSES */}
          <section>
            <div className="flex justify-between items-center">
              <h2 className="font-display text-2xl">Delivery address</h2>
              {addresses.length > 0 && !showNewAddr && (
                <button onClick={() => setShowNewAddr(true)} className="btn-gold-outline px-4 py-2 text-[10px] uppercase tracking-widest flex items-center gap-2" data-testid="add-address-btn">
                  <Plus size={12} /> New address
                </button>
              )}
            </div>

            {addresses.length > 0 && !showNewAddr && (
              <div className="mt-4 space-y-3">
                {addresses.map((a) => (
                  <button
                    key={a.id}
                    onClick={() => setSelectedAddr(a.id)}
                    className={"w-full text-left p-4 border transition-colors " + (selectedAddr === a.id ? "border-ink-900 bg-cream-200" : "hairline hover:bg-cream-200/60")}
                    data-testid={`address-${a.id}`}
                  >
                    <div className="flex justify-between items-start gap-4">
                      <div className="min-w-0">
                        <div className="text-sm font-medium">{a.name} · <span className="eyebrow ml-1">{a.label}</span></div>
                        <div className="text-sm text-ink-700 mt-1">{a.line1}{a.line2 ? ", " + a.line2 : ""}, {a.city}, {a.state} {a.pincode}</div>
                        <div className="text-xs text-ink-500 mt-1">{a.phone}</div>
                        {a.latitude && a.longitude && (
                          <a
                            href={`https://www.google.com/maps?q=${a.latitude},${a.longitude}`}
                            target="_blank" rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 mt-2 text-[11px] text-gold-500 hover:text-ink-900"
                          >
                            <MapPin size={11} /> View pinned location on map ↗
                          </a>
                        )}
                      </div>
                      {selectedAddr === a.id && <Check size={16} className="text-gold-500 mt-1 shrink-0" />}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {showNewAddr && (
              <div className="mt-6">
                <AddressForm
                  defaultName={user?.name || ""}
                  onSaved={async () => { await reload(); setShowNewAddr(false); }}
                  onCancel={addresses.length > 0 ? () => setShowNewAddr(false) : null}
                />
              </div>
            )}
          </section>

          {/* PAYMENT */}
          <section>
            <h2 className="font-display text-2xl">Payment</h2>
            <div className="mt-4 grid gap-3">
              <button
                type="button"
                onClick={() => setPayMethod("payu")}
                className={"text-left p-5 border-2 transition-colors flex items-start gap-3 " + (payMethod === "payu" ? "border-ink-900 bg-cream-50" : "hairline hover:bg-cream-200/60")}
                data-testid="pay-method-payu"
              >
                <span className={"mt-1 inline-block w-3 h-3 rounded-full " + (payMethod === "payu" ? "bg-ink-900" : "bg-cream-300")} />
                <span>
                  <span className="block text-sm font-medium">Pay online · Cards, UPI, Net Banking, Wallets</span>
                  <span className="block text-xs text-ink-500 mt-1">Secured by PayU India · order confirms instantly on successful payment.</span>
                </span>
              </button>
              <button
                type="button"
                onClick={() => setPayMethod("cod")}
                className={"text-left p-5 border-2 transition-colors flex items-start gap-3 " + (payMethod === "cod" ? "border-ink-900 bg-cream-50" : "hairline hover:bg-cream-200/60")}
                data-testid="pay-method-cod"
              >
                <span className={"mt-1 inline-block w-3 h-3 rounded-full " + (payMethod === "cod" ? "bg-ink-900" : "bg-cream-300")} />
                <span>
                  <span className="block text-sm font-medium">Cash on Delivery</span>
                  <span className="block text-xs text-ink-500 mt-1">Pay with cash or UPI when your order arrives.</span>
                </span>
              </button>
            </div>
          </section>
        </div>

        {/* SUMMARY */}
        <div className="lg:col-span-5">
          <div className="border hairline p-8 sticky top-28 bg-cream-50">
            <div className="eyebrow">Order summary</div>
            <div className="mt-6 space-y-3 max-h-64 overflow-auto pr-1">
              {items.map((it) => (
                <div key={it.key} className="flex gap-3">
                  <img src={it.image} alt="" className="w-14 h-16 object-cover bg-cream-200" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{it.name}</div>
                    <div className="text-[11px] text-ink-500">Qty {it.quantity}{it.subscription ? ` · ${it.subscription}` : ""}</div>
                  </div>
                  <div className="text-sm tabular-nums">₹{(it.price * it.quantity).toFixed(0)}</div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex gap-2">
              <input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Coupon code"
                className="flex-1 h-11 bg-white border hairline px-3 text-sm font-mono tracking-widest" data-testid="coupon-input" />
              <button onClick={applyCoupon} className="btn-gold-outline px-4 text-xs uppercase tracking-widest" data-testid="apply-coupon">Apply</button>
            </div>
            {applied && <div className="mt-2 text-[11px] text-olive-500">✓ {applied.code} · ₹{applied.discount.toFixed(0)} off</div>}

            <div className="mt-6 border-t hairline pt-4 space-y-2 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span className="tabular-nums">₹{totals.subtotal.toFixed(0)}</span></div>
              {discount > 0 && <div className="flex justify-between text-olive-500"><span>Discount</span><span className="tabular-nums">– ₹{discount.toFixed(0)}</span></div>}
              <div className="flex justify-between"><span>Shipping</span><span className="tabular-nums">{shipping ? `₹${shipping}` : "Free"}</span></div>
              <div className="mt-3 border-t hairline pt-3 flex justify-between font-display text-xl">
                <span>Total</span><span data-testid="checkout-total">₹{total.toFixed(0)}</span>
              </div>
            </div>

            <button onClick={placeOrder} disabled={placing || items.length === 0} className="mt-6 w-full btn-primary-ink py-4 text-xs uppercase tracking-[0.28em] disabled:opacity-50" data-testid="place-order-btn">
              {placing ? "Placing…" : (payMethod === "payu" ? "Pay now · Secure checkout" : "Place order · Cash on Delivery")}
            </button>
            <div className="mt-3 text-[11px] text-ink-500 text-center">By placing your order you agree to our terms & privacy policy.</div>
          </div>
        </div>
      </div>
    </div>
  );
}
